import Header from './components/Header';
import SkillsPalette from './components/SkillsPalette';
import Canvas from './components/Canvas';
import DetailsPanel from './components/DetailsPanel';
import TestsPanel from './components/TestsPanel';
import PlaybookEditCards from './components/PlaybookEditCards';
import ExportModal from './components/ExportModal';
import ForkModal from './components/ForkModal';
import SkillContentEditor from './components/SkillContentEditor';
import FileTree from './components/FileTree';
import ReviewPane from './components/ReviewPane';
import ProjectContextFiles from './components/ProjectContextFiles';
import ChatPanel from './components/ChatPanel';
import AnalyzeChat from './components/AnalyzeChat';
import AgentSettings from './components/AgentSettings';
import OutcomePromptDrawer from './components/OutcomePromptDrawer';
import type { useHome } from './useHome';
import { formatElapsed } from './runFlow';
import { shortModelLabel } from './runScorePanel';

type Props = ReturnType<typeof useHome>;

function TabBtn({
  active,
  onClick,
  icon,
  label,
  testId,
}: {
  active: boolean;
  onClick: () => void;
  icon: string;
  label: string;
  testId?: string;
}) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`whitespace-nowrap flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors relative h-full ${
        active ? 'text-textp' : 'text-texts hover:text-textp'
      }`}
    >
      <i className={icon} /> {label}
      {active && <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-accent" />}
    </button>
  );
}

export default function HomeView(s: Props) {
  const status = s.dirty ? 'unsaved' : 'ready';
  return (
    <div className="h-screen min-w-[1024px] flex flex-col overflow-hidden bg-bg text-textp">
      <Header
        pipelineName={s.pipelineName + (s.dirty ? '*' : '')}
        setPipelineName={s.setPipelineName}
        version={s.version}
        status={status as 'ready' | 'saved' | 'unsaved'}
        running={s.running}
        saving={s.saving}
        locked={s.running}
        hostedCredits={s.hostedCredits}
        runLabel={s.running ? `Running ${s.liveRun?.step_index ?? 0}/${s.liveRun?.step_total ?? 0} ${formatElapsed(s.elapsedMs)}` : undefined}
        onRun={s.onRun}
        onExport={s.onExport}
        onFork={s.onFork}
        onSave={s.onSave}
      />
      <div className="flex-1 flex min-h-0 overflow-hidden">
        <SkillsPalette
          skills={s.skills}
          previewSkill={s.previewSkill}
          onPreviewSkill={s.onPreviewSkill}
          onDragStart={() => {}}
          onResolve={s.onResolve}
        />
        {s.previewOutcome && (
          <OutcomePromptDrawer
            prompt={s.description}
            onChange={s.setDescription}
            onClose={() => s.onViewPrompt(false)}
            locked={s.running}
          />
        )}
        <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden">
          <div data-testid="studio-center-tabs" className="h-10 shrink-0 border-b border-border bg-bg flex items-center px-2 overflow-x-auto">
            <TabBtn active={s.centerTab === 'canvas'} onClick={() => s.setCenterTab('canvas')} icon="ri-flow-chart" label="Graph" />
            <TabBtn active={s.centerTab === 'content'} onClick={() => s.setCenterTab('content')} icon="ri-code-s-slash-line" label="Skill Content" />
            <TabBtn testId="files-tab" active={s.centerTab === 'files'} onClick={() => s.setCenterTab('files')} icon="ri-folder-3-line" label="Files" />
            <TabBtn testId="artifacts-tab" active={s.centerTab === 'artifacts'} onClick={() => s.setCenterTab('artifacts')} icon="ri-file-download-line" label="Artifacts" />
            <TabBtn testId="review-tab" active={s.centerTab === 'review'} onClick={() => s.setCenterTab('review')} icon="ri-eye-line" label="Review" />
            <TabBtn testId="tests-tab" active={s.centerTab === 'tests'} onClick={() => s.setCenterTab('tests')} icon="ri-checkbox-circle-line" label="Tests" />
            <div className="ml-auto flex items-center shrink-0">
              <button
                data-testid={s.chatDocked ? 'chat-dock-button' : 'chat-fullscreen-button'}
                onClick={() => s.setChatDocked(!s.chatDocked)}
                title={s.chatDocked ? 'Dock chat to bottom' : 'Open chat fullscreen'}
                className={`whitespace-nowrap flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${
                  s.chatDocked ? 'text-textp hover:text-texts' : 'text-accent hover:text-accent-hover'
                }`}
              >
                <i className={s.chatDocked ? 'ri-collapse-diagonal-line' : 'ri-expand-diagonal-line'} />
                {s.chatDocked ? 'Dock' : 'Fullscreen'}
              </button>
            </div>
          </div>
          {s.chatDocked ? (
            s.centerTab === 'canvas' ? (
            <Canvas
              skills={s.skills}
              nodes={s.nodes}
              edges={s.edges}
              groups={s.groups}
              stickies={s.stickies}
              schedulers={s.schedulers}
              selectedId={s.selectedId}
              setSelectedId={s.setSelectedId}
              onNodeMove={s.onNodeMove}
              onDropSkill={s.onDropSkill}
              onDropGroup={s.onDropGroup}
              onDropSticky={s.onDropSticky}
              onDropScheduler={s.onDropScheduler}
              onMoveGroup={s.onMoveGroup}
              onMoveSticky={s.onMoveSticky}
              onUpdateGroupTitle={s.onUpdateGroupTitle}
              onUpdateGroupIterations={s.onUpdateGroupIterations}
              onRemoveGroup={s.onRemoveGroup}
              onUpdateSticky={s.onUpdateSticky}
              onRemoveSticky={s.onRemoveSticky}
              onMoveScheduler={s.onMoveScheduler}
              onUpdateScheduler={s.onUpdateScheduler}
              onRemoveScheduler={s.onRemoveScheduler}
              onEditNode={s.onEditNode}
              onViewSkill={s.onPreviewSkill}
              onRemoveNode={s.onRemove}
              previewSkillId={s.previewSkill?.id ?? null}
              running={s.running}
              locked={s.running}
              activeEdgeIds={s.activeEdgeIds}
              prompt={s.description}
              viewingPrompt={s.previewOutcome}
              onViewPrompt={() => s.onViewPrompt()}
            />
          ) : s.centerTab === 'content' ? (
            <SkillContentEditor
              selectedNode={s.selectedNode}
              skill={s.selectedNode ? s.skillMap[s.selectedNode.skillId] ?? null : null}
              onUpdateBody={s.onUpdateBody}
              locked={s.running}
            />
          ) : s.centerTab === 'files' ? (
            <ProjectContextFiles
              projectId={s.projectId}
              files={s.projectFiles}
              busy={s.projectFilesBusy}
              error={s.projectFilesError}
              onAdd={s.onAddProjectFiles}
              onRemove={s.onRemoveProjectFile}
            />
          ) : s.centerTab === 'artifacts' ? (
            <FileTree tree={s.artifactTree} focusId={s.artifactFocusId} outcomeId={s.outcomeId} outcomeSlug={s.outcomeSlug} />
          ) : s.centerTab === 'review' ? (
            <ReviewPane tree={s.artifactTree} outcomeId={s.outcomeId} outcomeSlug={s.outcomeSlug} />
          ) : s.centerTab === 'tests' ? (
            <div className="flex-1 flex flex-col min-h-0">
              <PlaybookEditCards
                edits={s.pendingPlaybookEdits}
                onApply={s.onApplyPlaybook}
                onDismiss={s.onDismissPlaybookEdits}
              />
              <TestsPanel
              score={s.score}
              running={s.running}
              runLogs={s.liveRun?.logs ?? []}
              elapsedMs={s.elapsedMs}
              liveRunId={s.liveRun?.run_id ?? null}
              liveModel={s.liveRun?.model ?? ''}
              livePromptTokens={s.liveRun?.prompt_tokens ?? null}
              userScore={s.userScore}
              llmScore={s.llmScore}
              reviewMd={s.reviewMd}
              scoreChecks={s.scoreChecks}
              scoreHistory={s.scoreHistory}
              testArtifacts={s.testArtifacts}
              onOpenArtifact={s.onOpenArtifact}
              scorePrompt={s.scorePrompt}
              canTrain={s.canTrain}
              trainEpoch={s.liveRun?.epoch ?? 0}
              onSubmitScore={s.onSubmitScore}
              onSkipScore={s.onSkipScore}
              onTrain={s.onTrain}
              onReviewNote={s.onReviewNote}
            />
            </div>
            ) : null
          ) : null}
          <div
            data-testid="chat-docked"
            className={
              s.chatDocked
                ? 'shrink-0 h-56 flex flex-col min-h-0 border-t border-border'
                : 'flex-1 flex flex-col min-h-0 overflow-hidden'
            }
          >
            <ChatPanel
              chat={s.activeChat!}
              models={s.models}
              loadedModels={s.loadedModels}
              onSetAgent={s.onSetChatAgent}
              onSetModel={s.onSetChatModel}
              onSend={s.onSendChat}
              onApplyCompose={s.onApplyComposeToCanvas}
              typing={s.typing}
              error={s.chatError}
              locked={s.running}
              hostedCredits={s.chatHostedCredits}
            />
          </div>
        </div>
        <aside className="w-80 shrink-0 border-l border-border bg-bg flex flex-col min-h-0 overflow-hidden">
          <div className="h-9 shrink-0 border-b border-border bg-bg flex items-center px-2">
            {s.centerTab === 'tests' ? (
              <TabBtn testId="analyze-tab" active={s.rightTab !== 'agent'} onClick={() => s.setRightTab('analyze')} icon="ri-chat-3-line" label="Analyze" />
            ) : (
              <TabBtn active={s.rightTab === 'pipeline'} onClick={() => s.setRightTab('pipeline')} icon="ri-flow-chart" label="Playbook" />
            )}
            {s.activeChat && (
              <TabBtn testId="agent-tab" active={s.rightTab === 'agent'} onClick={() => s.setRightTab('agent')} icon="ri-robot-line" label="Agent" />
            )}
          </div>
          <div className={`flex-1 min-h-0 ${s.rightTab === 'agent' || (s.centerTab !== 'tests') ? 'overflow-y-auto' : 'overflow-hidden flex flex-col'}`}>
            {s.rightTab === 'agent' && s.activeChat ? (
              <AgentSettings
                chat={s.activeChat}
                settings={s.llmSettings}
                models={s.models}
                loadedModels={s.loadedModels}
                savingSettings={s.savingSettings}
                settingsMsg={s.settingsMsg}
                hostedCredits={s.chatHostedCredits}
                onSetAgent={s.onSetChatAgent}
                onSetModel={s.onSetChatModel}
                onSetBaseUrl={s.onSetChatBaseUrl}
                onSetContext={s.onSetChatContext}
                onSetOpenAIKey={s.onSetOpenAIKey}
                onSetAnthropicKey={s.onSetAnthropicKey}
                openaiKey={s.openaiKey}
                anthropicKey={s.anthropicKey}
                onSaveSettings={s.onSaveSettings}
                onRefreshModels={s.onRefreshModels}
                projectId={s.projectId}
                projectFiles={s.projectFiles}
                projectFilesBusy={s.projectFilesBusy}
                projectFilesError={s.projectFilesError}
                onAddProjectFiles={s.onAddProjectFiles}
                onRemoveProjectFile={s.onRemoveProjectFile}
                onCreateSkill={s.onCreateSkill}
              />
            ) : s.centerTab === 'tests' ? (
              <AnalyzeChat
                messages={s.analyzeMessages}
                onSend={s.onSendAnalyzeChat}
                typing={s.analyzeTyping}
                error={s.analyzeError}
                hostedCredits={s.chatHostedCredits}
                modelLabel={shortModelLabel(s.activeChat?.model || '')}
                playbookOps={s.analyzePlaybookOps}
                history={s.playbookHistory}
                onApplyPlaybook={s.onApplyAnalyzePlaybook}
                onFork={s.onForkPlaybook}
                onRestore={s.onRestorePlaybook}
                onApplyCompose={s.onApplyComposeToCanvas}
              />
            ) : (
              <DetailsPanel
                selectedNode={s.selectedNode}
                skills={s.skills}
                nodes={s.nodes}
                pipelineName={s.pipelineName}
                setPipelineName={s.setPipelineName}
                description={s.description}
                setDescription={s.setDescription}
                setSelectedId={s.setSelectedId}
                onRunStep={s.onRunStep}
                onRemove={s.onRemove}
                onUpdateNote={s.onUpdateNote}
                score={s.score}
                locked={s.running}
                running={s.running}
                runLogs={s.liveRun?.logs ?? []}
              />
            )}
          </div>
        </aside>
      </div>
      <footer className="h-8 shrink-0 border-t border-border bg-bg px-4 flex items-center justify-between text-[11px] text-texts">
        <div className="flex items-center gap-1.5">
          <span>Pipeline: {s.nodes.length} steps</span>
          {s.groups.length > 0 && (<><span className="text-border">·</span><span>{s.groups.length} {s.groups.length === 1 ? 'group' : 'groups'}</span></>)}
          <span className="text-border">·</span>
          <span>Last run: {s.lastRun}</span>
          <span className="text-border">·</span>
          <span>Score: <span className="text-textp font-mono">{s.score}</span>{s.llmScore != null ? <span> · LLM {s.llmScore}/12</span> : null}</span>
        </div>
      </footer>
      {s.showExport && (
        <ExportModal
          onClose={() => s.setShowExport(false)}
          pipelineName={s.pipelineName}
          description={s.description}
          nodes={s.nodes}
          edges={s.edges}
          groups={s.groups}
          skills={s.skills}
          score={s.score}
          lastRun={s.lastRun}
          outcomeId={s.outcomeId}
        />
      )}
      {s.showFork && (
        <ForkModal
          currentName={s.pipelineName}
          onClose={() => s.setShowFork(false)}
          onConfirm={(name) => { s.setPipelineName(name); s.setShowFork(false); }}
        />
      )}
    </div>
  );
}
